import React from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faAngleLeft,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import TopHeader from "./top-header.js";
import { ButtonStyle } from "./menu";

const StyledMenu = styled.div`
  transition: transform 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  transform: ${({ $burgerOpen }) =>
    $burgerOpen ? "translateX(0%)" : "translateX(-100%)"};
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || `var(--color-secondary)`};
  font-family: var(--font-links);
  overflow-x: hidden;
  width: 450px;
  flex-direction: column;
  justify-content: space-between;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  align-items: center;
  height: 100vh;
  padding: 0;
  margin: 0;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.16);
  z-index: 9999;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100vw;
  }
`;

const MenuWrapper = styled.ul`
  position: relative;
  padding-inline: 2rem;
  width: 100%;
  height: 100%;
  margin-top: 4rem;
  font-size: 1.1rem;
  overflow-x: hidden;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const BaseButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  text-decoration: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  color: var(--color-black);
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const HasChildren = styled(BaseButton)`
  display: flex;
  align-items: baseline;
  color: var(--color-header-link) || var(--color-black);
`;

const NavItem = styled.li`
  list-style-type: none;
  display: flex;
  flex-direction: column;
  font-weight: ${($isBold) => ($isBold ? "500" : "unset")};
  width: 100%;
  a {
    margin-top: 0;
    margin-bottom: 0;
  }
  & > * {
    padding-block: 0;
    display: flex;
    align-items: center;
    min-height: 3rem;
  }
`;
const UlSubLinks = styled.ul`
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  padding-block: 1.5rem;
  padding-inline: 0;
  border-top: 1px solid var(--color-accent2);
  width: 100%;
  height: fit-content;
`;
const Dropdown = styled.div`
  --transition-duration: 0.4s;
  display: flex;
  overflow-y: scroll;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || `var(--color-secondary)`};
  transition-property: transform;
  transition-timing-function: ease-in-out;
  transition-duration: ${({ $mainMenuIsOpening }) =>
    $mainMenuIsOpening ? "0s" : "var(--transition-duration)"};
  transition-delay: ${({ $mainMenuIsClosing }) =>
    $mainMenuIsClosing ? "var(--transition-duration)" : "0s"};
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  position: absolute;
  padding-inline: 40px;
  inset: 0;
  align-items: flex-start;
  overflow-y: scroll;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
const ExtraLink = styled(NavLink)`
  padding-top: 3rem;
  padding-bottom: 2rem;
`;
const BottomContent = styled.div`
  bottom: 0;
  background: #f6f6f6;
  width: 100%;
  padding: 1rem 2rem;
  display: flex;
  flex-flow: column wrap;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1rem;
  }
`;
const BackButton = styled(BaseButton)`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-family: var(--font-paragraphs);
  min-height: 3rem;
  padding: 0;
`;
const CloseButton = styled(BaseButton)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  padding: 4px 7px;
  border-radius: var(--border-radius);
  transition: all 0.3s ease-in-out;
  z-index: 99;
  @media (hover: hover) {
    &:hover {
      background: #ddd;
    }
  }
`;
const SideNavLinks = styled.div`
  margin-top: 2rem;
  font-size: 0.875rem;
`;

const SlideNav = ({ links, open, onClose, burgerOpen, mainMenuState }) => {
  const [activeChild, setActiveChild] = React.useState();

  React.useEffect(() => {
    setActiveChild(undefined);
  }, [burgerOpen]);

  return (
    <Dropdown
      $isOpen={open}
      $mainMenuIsOpening={mainMenuState === "opening"}
      $mainMenuIsClosing={mainMenuState === "closing"}
    >
      <BackButton onClick={onClose}>
        <FontAwesomeIcon
          icon={faAngleLeft}
          size="xs"
          style={{ marginRight: "10px" }}
        />
        Tilbake
      </BackButton>
      <ExtraLink to={links.url}>
        {links.description}
        <FontAwesomeIcon
          icon={faAngleRight}
          size="xs"
          style={{ marginLeft: "10px" }}
        />
      </ExtraLink>
      <UlSubLinks>
        {links.subLinks.map((item) => (
          <NavItem key={item.id}>
            {item.subLinks?.length > 0 ? (
              <>
                <HasChildren onClick={() => setActiveChild(item.id)}>
                  {item.title}
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    size="xs"
                    style={{ marginLeft: "10px" }}
                  />
                </HasChildren>
                <SlideNav
                  links={item}
                  open={activeChild === item.id}
                  onClose={() => setActiveChild(undefined)}
                  burgerOpen={burgerOpen}
                  mainMenuState={mainMenuState}
                />
              </>
            ) : (
              <NavLink to={item.url}>{item.title}</NavLink>
            )}
          </NavItem>
        ))}
      </UlSubLinks>
    </Dropdown>
  );
};

const MenuSideNav = ({ setBurgerOpen, burgerOpen, headerNav }) => {
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [mainMenuState, setMainMenuState] = React.useState(
    burgerOpen ? "opened" : "closed"
  );

  React.useEffect(() => {
    const handleEscape = (e) => e.keyCode === 27 && setBurgerOpen(undefined);
    window.addEventListener("keyup", handleEscape);
    return () => window.removeEventListener("keyup", handleEscape);
  }, []);

  React.useEffect(() => {
    setActiveDropdown(burgerOpen);
    if (!activeDropdown) {
      if (burgerOpen) {
        setMainMenuState("opening");
      }
    } else {
      if (!burgerOpen) {
        setMainMenuState("closing");
      }
    }
  }, [burgerOpen]);

  const [navItems] = React.useState(() =>
    headerNav?.menuItems.nodes
      .filter((node) => node.parentId === null)
      .map((node) => {
        return {
          id: node.id,
          url: node.url,
          title: node.label,
          description: node.description,
          isButton: node.cssClasses.includes("button"),
          isSideMenu: node.cssClasses.includes("sidebar-menu"),
          subLinks: node.childItems?.nodes?.map((node) => ({
            id: node.id,
            url: node.url,
            title: node.label,
            description: node.description,
            parentId: node.parentId,
            subLinks: node.childItems?.nodes?.map((node) => ({
              id: node.id,
              url: node.url,
              title: node.label,
              description: node.description,
              parentId: node.parentId,
              subLinks: node.childItems?.nodes?.map((node) => ({
                id: node.id,
                url: node.url,
                title: node.label,
                description: node.description,
                parentId: node.parentId,
                subLinks: node.childItems?.nodes?.map((node) => ({
                  id: node.id,
                  url: node.url,
                  title: node.label,
                  description: node.description,
                  parentId: node.parentId,
                })),
              })),
            })),
          })),
        };
      })
  );

  const [menuItems] = React.useState(() =>
    navItems?.filter((item) => !item.isButton && !item.isSideMenu)
  );
  const [extraLinks] = React.useState(() =>
    navItems?.filter((item) => item.isSideMenu)
  );
  const [buttons] = React.useState(() =>
    navItems?.filter((item) => item.isButton)
  );

  return (
    <StyledMenu
      $burgerOpen={burgerOpen !== undefined}
      onTransitionEnd={() => setMainMenuState(burgerOpen ? "opened" : "closed")}
    >
      <CloseButton onClick={() => setBurgerOpen(undefined)}>
        <FontAwesomeIcon size="xl" icon={faTimes} />
      </CloseButton>
      <MenuWrapper>
        {menuItems?.map((node) => (
          <NavItem key={node.id} $isBold={true}>
            {node.subLinks?.length > 0 ? (
              <HasChildren to="" onClick={() => setActiveDropdown(node.id)}>
                {node.title}
                <FontAwesomeIcon
                  icon={faAngleRight}
                  size="xs"
                  style={{ marginLeft: "10px" }}
                />
              </HasChildren>
            ) : (
              <NavLink to={node.url}>{node.title}</NavLink>
            )}
            {node.subLinks?.length > 0 && (
              <SlideNav
                links={node}
                open={activeDropdown === node.id}
                onClose={() => setActiveDropdown(undefined)}
                burgerOpen={burgerOpen}
                mainMenuState={mainMenuState}
              />
            )}
          </NavItem>
        ))}
        {extraLinks?.length > 0 && (
          <SideNavLinks>
            {extraLinks.map((link) => (
              <NavItem key={link.id} $isBold={true}>
                <NavLink to={link.url}>{link.title}</NavLink>
              </NavItem>
            ))}
          </SideNavLinks>
        )}
      </MenuWrapper>
      <BottomContent>
        {buttons?.map((button) => (
          <ButtonStyle key={button.id} to={button.url}>
            {button.title}
          </ButtonStyle>
        ))}
        <TopHeader sideMenu={true} />
      </BottomContent>
    </StyledMenu>
  );
};

MenuSideNav.propTypes = {
  setBurgerOpen: PropTypes.func,
  burgerOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  headerNav: PropTypes.object,
};

SlideNav.propTypes = {
  links: PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
    subLinks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        subLinks: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  burgerOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  mainMenuState: PropTypes.oneOf(["opening", "closing", "closed", "opened"]),
};

export default MenuSideNav;
